import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Trash2, Brush } from "lucide-react";

export default function StylesPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [styles, setStyles] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", prompt: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/styles").then((r) => setStyles(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/styles", form);
      toast.success("Brand style created");
      setShowCreate(false);
      setForm({ name: "", description: "", prompt: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this brand style?")) return;
    try {
      await api.delete(`/styles/${id}`);
      toast.success("Style deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">Customization</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">Brand Styles</h1>
          <p className="text-zinc-500 mt-2 text-sm">Reusable aesthetic presets applied to every generation — pick them in any studio.</p>
        </div>
        {isAdmin && (
          <button data-testid="styles-create-button" onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors">
            <Plus size={15} /> New style
          </button>
        )}
      </div>

      {styles.length === 0 && (
        <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400" data-testid="styles-empty-state">
          <Brush size={26} className="mx-auto mb-3" />
          <p className="text-sm">{isAdmin ? "No brand styles yet — create your first aesthetic preset." : "No brand styles yet — ask an admin to create one."}</p>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {styles.map((s) => (
          <div key={s.id} data-testid={`style-card-${s.id}`} className="border border-zinc-200 bg-white rounded-lg p-6 hover:shadow-sm transition-shadow">
            <div className="flex items-start justify-between">
              <h3 className="font-heading font-semibold">{s.name}</h3>
              {isAdmin && (
                <button data-testid={`style-delete-${s.id}`} onClick={() => remove(s.id)}
                  className="p-1.5 rounded-md text-zinc-400 hover:bg-red-50 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
              )}
            </div>
            {s.description && <p className="text-sm text-zinc-500 mt-1">{s.description}</p>}
            <p className="text-xs text-zinc-400 mt-3 border-t border-zinc-100 pt-3 leading-relaxed">"{s.prompt}"</p>
            <p className="text-[11px] text-zinc-400 mt-3">By {s.created_by}</p>
          </div>
        ))}
      </div>

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-lg p-8 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="style-create-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading font-semibold text-lg">New brand style</h2>
              <button data-testid="style-create-close" onClick={() => setShowCreate(false)} className="p-1.5 hover:bg-zinc-100 rounded-md"><X size={16} /></button>
            </div>
            <div className="space-y-4">
              <input data-testid="style-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Style name — e.g. Minimal Scandi"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <input data-testid="style-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description (optional)"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
              <textarea data-testid="style-prompt-input" value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} rows={4}
                placeholder="Aesthetic directive — e.g. clean lines, muted earth tones, matte natural fabrics, soft diffused lighting, understated luxury"
                className="w-full border border-zinc-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950 resize-none" />
              <button data-testid="style-save-button" onClick={create} disabled={!form.name || !form.prompt || saving}
                className="w-full bg-zinc-950 text-white py-3 rounded-md font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
                {saving ? "Creating…" : "Create style"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
